<template>
  <div
    v-if="parentType"
    :data-track-id="uuid"
    :class="{
      'border-b border-b-border py-4 tablet:py-7 desktop:py-10': isDivided,
    }"
  >
    <UnpublishedWarning
      v-if="user.canAccessToolbar && !teaserProps?.isPublished"
    />
    <Component
      :is="teaserComponent"
      v-bind="teaserProps"
      :class="{
        'has-number': isNumbered,
      }"
    >
      <template v-if="options.teaserMedia === 'zurichCard'" #media>
        <ZurichCardImage
          :id="uuid"
          :no-animation="isEditing"
          class="block aspect-4/3"
        />
      </template>
    </Component>
  </div>
  <section v-else :id class="container">
    <UnpublishedWarning
      v-if="user.canAccessToolbar && !teaserProps?.isPublished"
    />
    <ZtLink
      v-if="teaserProps"
      :to="teaserProps.url"
      class="paragraph-teaser-full group-link group/teaser relative my-spacing-md block overflow-hidden"
    >
      <MediaImage
        v-if="teaserProps.media"
        v-bind="teaserProps.media"
        responsive-style="content_fullwidth"
        class="transition duration-300 ease-swing group-hover/teaser:scale-105"
      />
      <div
        class="paragraph-teaser-full-text absolute bottom-0 left-0 w-full p-8"
      >
        <h3 class="title is-h2 !mb-0 text-white">{{ teaserProps.title }}</h3>
        <p
          v-if="teaserProps.text"
          class="mt-3 max-w-prose text-balance text-prose text-white"
        >
          {{ teaserProps.text }}
        </p>
        <div
          class="link with-arrow mt-5 text-prose font-bold text-white group-hover/teaser:text-white"
        >
          {{ teaserProps.linkTitle || $texts('teaserMore', 'More') }}
        </div>
      </div>
    </ZtLink>
  </section>
</template>

<script lang="ts" setup>
import type { ParagraphTeaserFragment } from '#graphql-operations'
import type { TeaserProps } from '~/components/Teaser/types'
import {
  TeaserFull,
  TeaserHalf,
  TeaserThird,
  TeaserQuarter,
  TeaserFifth,
} from '#components'
import { buildTeaserProps } from './helper'

const props = defineProps<ParagraphTeaserFragment>()

const { $texts } = useEasyTexts()
const user = await useDrupalUser()

const { options, isEditing, parentType, index, siblings } = defineBlokkli({
  bundle: 'teaser',
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
  options: {
    teaserMedia: {
      type: 'radios',
      label: 'Teaser Media',
      default: 'media',
      group: 'Advanced',
      options: {
        media: 'Media',
        zurichCard: 'Zürich Card',
      },
    },
    showOpeningHours: {
      type: 'checkbox',
      label: 'Show opening hours',
      default: true,
    },
  },

  editor: {
    previewWidth: 800,
  },
})
const id = useParagraphAnchorId(index, siblings)

const teaserColumns = useParagraphContext('teaserColumns', 'full')
const isDivided = useParagraphContext('teaserDivided', false)
const isNumbered = useParagraphContext('teaserNumbered', false)

const teaserComponent = computed(() => {
  switch (teaserColumns.value) {
    case 'full':
      return TeaserFull
    case 'quarter':
      return TeaserQuarter
    case 'fifth':
      return TeaserFifth
    case 'half':
      return TeaserHalf
  }

  return TeaserThird
})

const teaserProps = computed<TeaserProps | null>(() => {
  if (!props) {
    return null
  }
  const teaserProps = buildTeaserProps(props)
  if (!teaserProps) {
    return null
  }

  return {
    ...teaserProps,
    showOpeningHours: options.value.showOpeningHours,
  }
})
</script>

<style lang="postcss">
.paragraph-teaser-full-text {
  background: linear-gradient(
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0.5) 40%,
    rgba(0, 0, 0, 0.8)
  );
}
</style>
